import React from "react";
import "./Page.css";

function Privacy() {
  return (
    <div className="page">
      <div className="section-content" style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem' }}>
        <header>
          <h1 className="title fw-bold">PRIVACY POLICY</h1>
        </header>

        <section>
          <p>
            This privacy notice describes how and why we collect, store, use, and/or share ("process") your information when you use our 
            golf tournament registration services ("Services"), including when you:
          </p>
          <ul>
            <li>
              Visit our website to register for the Toke For Stroke Open golf tournament
            </li>
            <li>
              Register for event spots and make payments online
            </li>
            <li>
              Contact us regarding the event or registration
            </li>
          </ul>
          <p>
            Questions or concerns? Reading this privacy notice will help you understand your privacy rights and choices. 
            If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, 
            please contact us at{" "}
            <a href="mailto:enterprisewebsolutions@outlook.com">
              enterprisewebsolutions@outlook.com
            </a>
            .
          </p>
        </section>

        <section>
          <h2>SUMMARY OF KEY POINTS</h2>
          <p>
            This summary provides key points from our privacy notice, but you can find out more details about any of these topics below.
          </p>
          <ul>
            <li>
              <strong>What personal information do we process?</strong> When you register for the golf tournament event, we collect 
              information such as your name, email address, phone number, and payment information necessary to process your registration.
            </li>
            <li>
              <strong>Do we process any sensitive personal information?</strong> We do not process sensitive personal information beyond 
              payment data required for transaction processing.
            </li>
            <li>
              <strong>Do we receive any information from third parties?</strong> We do not receive any information from third parties.
            </li>
            <li>
              <strong>How do we process your information?</strong> We process your information to process your event registration, 
              communicate with you about the event, process payments, and comply with legal obligations.
            </li>
            <li>
              <strong>How do we keep your information safe?</strong> We have implemented appropriate technical and organizational security 
              measures designed to protect your personal information.
            </li>
          </ul>
        </section>

        <section>
          <h2>1. WHAT INFORMATION DO WE COLLECT?</h2>
          <p>
            <strong>Personal information you disclose to us</strong>
          </p>
          <p>
            <em>
              In Short: We collect personal information that you provide to us when you register for the golf tournament event.
            </em>
          </p>
          <p>
            We collect personal information that you voluntarily provide to us when you register for the Toke For Stroke Open golf tournament, 
            including when you complete the registration form, make a payment, or contact us about the event.
          </p>
          <p>
            <strong>Personal Information Provided by You.</strong> The personal information that we collect includes:
          </p>
          <ul>
            <li>Contact name (primary registrant)</li>
            <li>Team name (if applicable)</li>
            <li>Player names (all participants in your group)</li>
            <li>Phone numbers</li>
            <li>Email addresses</li>
            <li>Number of players/spot registrations</li>
          </ul>
          <p>
            <strong>Payment Data.</strong> We collect data necessary to process your payment for event registration, including your payment 
            instrument number and the security code associated with your payment instrument. All payment data is processed securely through our 
            payment processor. We do not store full credit card numbers on our servers.
          </p>
          <p>
            All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to 
            such personal information.
          </p>
        </section>

        <section>
          <h2>2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
          <p>
            <em>
              In Short: We process your information to process your event registration, communicate with you about the event, 
              process payments, and comply with legal obligations.
            </em>
          </p>
          <p>
            We process your personal information for a variety of reasons, depending on how you interact with our Services, including:
          </p>
          <ul>
            <li>
              <strong>To process your event registration.</strong> We use your information to register you and your team for the golf tournament, 
              process your payment, and confirm your spot in the event.
            </li>
            <li>
              <strong>To communicate with you.</strong> We use your contact information to send you registration confirmations, event updates, 
              schedule information, and other communications related to the tournament.
            </li>
            <li>
              <strong>To manage event operations.</strong> We use your information to organize the event, create participant lists, 
              and coordinate with the golf course.
            </li>
            <li>
              <strong>To process payments.</strong> We use your payment information to process registration fees securely.
            </li>
            <li>
              <strong>To comply with legal obligations.</strong> We may process your information to comply with applicable laws and regulations.
            </li>
          </ul>
        </section>

        <section>
          <h2>3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
          <p>
            <em>
              In Short: We may share information in specific situations described in this section.
            </em>
          </p>
          <p>
            We may need to share your personal information in the following situations:
          </p>
          <ul>
            <li>
              <strong>Event Operations.</strong> We may share participant names and team information with Berkshire Valley Golf Course 
              and event organizers for event coordination purposes.
            </li>
            <li>
              <strong>Payment Processing.</strong> We share payment information with our payment processor to complete transactions.
            </li>
            <li>
              <strong>Legal Requirements.</strong> We may share information if required by law or to protect our rights and the rights of others.
            </li>
          </ul>
          <p>
            We do not sell your personal information to third parties.
          </p>
        </section>

        <section>
          <h2>4. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
          <p>
            <em>
              In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this privacy notice 
              unless otherwise required by law.
            </em>
          </p>
          <p>
            We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy notice, 
            unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements).
          </p>
          <p>
            We typically retain registration information for up to one year after the event date for record-keeping and communication purposes.
          </p>
        </section>

        <section>
          <h2>5. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
          <p>
            <em>
              In Short: We aim to protect your personal information through a system of organizational and technical security measures.
            </em>
          </p>
          <p>
            We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any 
            personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission 
            over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that 
            hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, 
            steal, or modify your information.
          </p>
          <p>
            Although we will do our best to protect your personal information, transmission of personal information to and from our Services is 
            at your own risk. You should only access the Services within a secure environment.
          </p>
        </section>

        <section>
          <h2>6. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
          <p>
            <em>
              In Short: You may review, change, or request deletion of your information at any time.
            </em>
          </p>
          <p>
            If you would like to review or change the information in your registration, or if you wish to request deletion of your information, 
            you can contact us using the details provided in the "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" section below.
          </p>
          <p>
            If you have questions or comments about your privacy rights, you may email us at{" "}
            <a href="mailto:enterprisewebsolutions@outlook.com">
              enterprisewebsolutions@outlook.com
            </a>
            .
          </p>
        </section>

        <section>
          <h2>7. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
          <p>
            If you have questions or comments about this notice, you may email us at{" "}
            <a href="mailto:enterprisewebsolutions@outlook.com">
              enterprisewebsolutions@outlook.com
            </a>{" "}
            or contact us by post at:
          </p>
          <p>
            <strong>Enterprise Web Solutions LLC</strong>
            <div>28 Gristmill, Sparta, NJ 07871 United States</div>
          </p>
        </section>

        <p>Last updated: January 2025</p>
      </div>
    </div>
  );
}

export default Privacy;
